import React, { useEffect, useState } from 'react';
import { PlaySolidIcon } from '@mentimeter/ragnar-visuals';
import type { FunctionComponent } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Tooltip } from '@mentimeter/ragnar-ui/tooltip';
import type { ButtonT } from '@mentimeter/ragnar-ui/button';
import { Prompt } from '@mentimeter/ragnar-ui/prompt';
import { P } from '@mentimeter/ragnar-ui/typography';

interface OverviewListItemPlayButtonProps extends Pick<ButtonT, 'mt'> {
  questionId: string;
  presenterName?: string | undefined;
  isSomeonePresenting?: boolean | undefined;
  onStartPresenting?: ((questionId: string) => void) | undefined;
}

export const OverviewListItemPlayButton: FunctionComponent<
  OverviewListItemPlayButtonProps
> = ({
  questionId,
  isSomeonePresenting,
  presenterName,
  onStartPresenting,
  ...layoutProps
}) => {
  const [presenterNameShown, setPresenterNameShown] = useState(
    presenterName || 'Someone else',
  );

  useEffect(() => {
    // This is to prevent presenterName being undefined propagating to the other users (when they have the popover open)
    // when presenter is ending their presentation
    if (presenterName) {
      setPresenterNameShown(presenterName);
    }
  }, [presenterName]);

  const tooltipContent = isSomeonePresenting ? (
    <Prompt
      width="300px"
      p="space4"
      theme="dark"
      title={`${presenterNameShown} is currently presenting`}
    >
      <P mt="2">
        Starting this presentation now might interrupt
        {presenterNameShown} and affect voting results.
      </P>
      <P>
        If you’d also like to present now, you can duplicate this presentation.
      </P>
    </Prompt>
  ) : onStartPresenting ? (
    'This is the slide currently seen in voting. Click to present from here.'
  ) : null;

  return (
    <Tooltip
      side="right"
      className={
        isSomeonePresenting ? 'dark bg-surface fill-surface' : undefined
      }
      trigger={
        <Box
          id={`${questionId}-play-icon`}
          aria-hidden
          alignItems="center"
          {...layoutProps}
        >
          <Clickable
            onClick={(e) => {
              e.stopPropagation();
              onStartPresenting?.(questionId);
            }}
            disabled={!onStartPresenting || isSomeonePresenting}
            aria-label="Present from this slide"
          >
            <PlaySolidIcon color="primary" size={0} />
          </Clickable>
        </Box>
      }
    >
      {tooltipContent}
    </Tooltip>
  );
};
